import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Paper,
  Tooltip,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ListAlt,
  HelpOutline,
} from "@mui/icons-material";
import { debounce } from "lodash";
import { v4 as uuidv4 } from "uuid";
import { Tables } from "../../../database.types";
import { StandardButton } from "../../components/StandardButton";
import { StandardTextInput } from "../../components/StandardTextInput";
import {
  AutoSaveIndicator,
  AutoSaveStatus,
} from "../../components/AutoSaveIndicator";
import { useSupabase } from "../../auth/useSupabase";
import { gameService } from "../../data/gameService";

type ClassicBonusAnswerRow = Tables<"classic_bonus_answers">;

interface ClassicBonusEditorProps {
  bonusRoundId: string;
  disabled: boolean;
}

const QUESTION_DEBOUNCE_MS = 400;
const ANSWERS_DEBOUNCE_MS = 600;

export const ClassicBonusEditor = ({
  bonusRoundId,
  disabled,
}: ClassicBonusEditorProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { supabase } = useSupabase();

  const [loading, setLoading] = useState(true);
  const [questionText, setQuestionText] = useState("");
  const [answers, setAnswers] = useState<ClassicBonusAnswerRow[]>([]);

  // Auto-save indicator
  const [autoSaveState, setAutoSaveState] = useState<AutoSaveStatus>("idle");
  const savedTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const markSaving = useCallback(() => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    setAutoSaveState("saving");
  }, []);

  const markSaved = useCallback(() => {
    setAutoSaveState("saved");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 2000);
  }, []);

  const markError = useCallback(() => {
    setAutoSaveState("error");
    savedTimerRef.current = setTimeout(() => setAutoSaveState("idle"), 3000);
  }, []);

  // ── Fetch on mount ────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const [bonusRound, fetchedAnswers] = await Promise.all([
          gameService.getBonusRound(supabase, bonusRoundId),
          gameService.getClassicBonusAnswers(supabase, bonusRoundId),
        ]);
        if (cancelled) return;

        setQuestionText(bonusRound?.question_text ?? "");

        if (fetchedAnswers.length === 0) {
          setAnswers([
            {
              id: uuidv4(),
              bonus_round_id: bonusRoundId,
              answer_text: "",
              correct_team_id: null,
              sort_order: 0,
              created_at: null,
            },
          ]);
        } else {
          setAnswers(fetchedAnswers);
        }
      } catch (err) {
        console.error("Error loading classic bonus data:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [supabase, bonusRoundId]);

  // ── Debounced saves ───────────────────────────────────────────────────

  const debouncedSaveQuestion = useMemo(
    () =>
      debounce(async (text: string) => {
        markSaving();
        try {
          await gameService.setBonusQuestionText(supabase, bonusRoundId, text);
          markSaved();
        } catch {
          markError();
        }
      }, QUESTION_DEBOUNCE_MS),
    [supabase, bonusRoundId, markSaving, markSaved, markError],
  );

  const debouncedSaveAnswers = useMemo(
    () =>
      debounce(async (currentAnswers: ClassicBonusAnswerRow[]) => {
        markSaving();
        try {
          await gameService.replaceClassicBonusAnswers(
            supabase,
            bonusRoundId,
            currentAnswers.map((a) => ({ answer_text: a.answer_text })),
          );
          markSaved();
        } catch {
          markError();
        }
      }, ANSWERS_DEBOUNCE_MS),
    [supabase, bonusRoundId, markSaving, markSaved, markError],
  );

  // Flush pending saves on unmount
  useEffect(() => {
    return () => {
      debouncedSaveQuestion.flush();
      debouncedSaveAnswers.flush();
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    };
  }, [debouncedSaveQuestion, debouncedSaveAnswers]);

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleQuestionChange = (value: string) => {
    setQuestionText(value);
    debouncedSaveQuestion(value);
  };

  const handleAnswerChange = (index: number, value: string) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], answer_text: value };
      debouncedSaveAnswers(updated);
      return updated;
    });
  };

  const addAnswer = () => {
    setAnswers((prev) => {
      const updated = [
        ...prev,
        {
          id: uuidv4(),
          bonus_round_id: bonusRoundId,
          answer_text: "",
          correct_team_id: null,
          sort_order: prev.length,
          created_at: null,
        },
      ];
      debouncedSaveAnswers(updated);
      return updated;
    });
  };

  const removeAnswer = (index: number) => {
    setAnswers((prev) => {
      const updated = [...prev];
      updated.splice(index, 1);
      debouncedSaveAnswers(updated);
      return updated;
    });
  };

  // ── Loading state ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" alignItems="center" gap={1}>
        <AutoSaveIndicator status={autoSaveState} />
      </Box>

      <StandardTextInput
        label="Bonus Round Question"
        value={questionText}
        onChange={(e) => handleQuestionChange(e.target.value)}
        fullWidth
        disabled={disabled}
        multiline
        rows={2}
        required
        inputProps={{ "data-testid": "bonus-round-question-input" }}
        InputProps={{
          startAdornment: (
            <HelpOutline color="primary" sx={{ mr: 1, mb: 2 }} />
          ),
        }}
        placeholder="Enter the main question for the bonus round"
      />

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, sm: 3 },
          borderRadius: 2,
          backgroundColor: "rgba(255, 255, 255, 0.03)",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "rgba(255, 255, 255, 0.7)",
            fontWeight: 600,
          }}
        >
          <ListAlt fontSize="small" />
          Answers ({answers.length})
        </Typography>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
            },
            gap: 0.75,
          }}
        >
          {answers.map((answer, index) => (
            <Box
              key={answer.id}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 0.75,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Chip
                label={index + 1}
                size="small"
                color="primary"
                sx={{ mr: 0.75, minWidth: 28, flexShrink: 0 }}
              />
              <StandardTextInput
                label={`Answer ${index + 1}`}
                id={`classic-answer-input-${index}`}
                value={answer.answer_text}
                onChange={(e) => handleAnswerChange(index, e.target.value)}
                fullWidth
                disabled={disabled}
                size="small"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    addAnswer();
                    setTimeout(() => {
                      const next = document.getElementById(
                        `classic-answer-input-${index + 1}`,
                      );
                      next?.focus();
                      next?.scrollIntoView({ behavior: "smooth" });
                    }, 50);
                  }
                }}
              />
              <Tooltip title="Remove Answer">
                <IconButton
                  color="error"
                  onClick={() => removeAnswer(index)}
                  sx={{ ml: 0.5, flexShrink: 0 }}
                  size="small"
                  disabled={disabled}
                  tabIndex={-1}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))}
        </Box>

        <Box
          display="flex"
          width="100%"
          justifyContent="flex-end"
          alignItems="center"
          gap={1}
        >
          <Typography variant="body2" color="gray">
            Press Enter to add answer
          </Typography>
          <StandardButton
            variant="outlined"
            disabled={disabled}
            color="secondary"
            onClick={addAnswer}
            size={isMobile ? "small" : "medium"}
          >
            <AddIcon />
          </StandardButton>
        </Box>
      </Paper>
    </Box>
  );
};
